/* eslint-disable jsx-a11y/label-has-associated-control */
import React from "react";
import { useSearch, createFileRoute } from "@tanstack/react-router";
import utilities from "@/constants/utilities";
import { Loader2Icon } from "lucide-react";
import { DateTime } from "luxon";
import { createBayouCustomer, createUtilityApiForm } from "@/api/bayou";
import { getLocalStorageAuth, updateLocalStorageAuth } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { UTILITY_REDIRECT_URI, utilityEnv } from "@/lib/utilityEnv";

export const LOCALSTORAGE_AUTHS_KEY = "utilityAuthsInProgress";

const NewUtilityConnection = () => {
  const {
    connType,
    utilityCode,
    buildingId,
    isTest = false,
  } = useSearch({
    from: "/external/new_utility_connection",
  });
  const utility = utilities[utilityCode];
  const currentAuth = getLocalStorageAuth(connType, utilityCode, true);
  currentAuth.status = "STARTED";
  currentAuth.buildingId = buildingId;
  currentAuth.updated_on = DateTime.now().toISOTime();
  updateLocalStorageAuth(connType, utilityCode, currentAuth);

  let queryFn;
  if (connType === "bayou") {
    queryFn = () =>
      createBayouCustomer({
        buildingId,
        utilityCode,
        bayouUtilityId: utility.bayouId,
        isTest,
      });
  } else if (connType === "utilityApi") {
    queryFn = () =>
      createUtilityApiForm({
        buildingId,
        utilityCode,
        utilityApiUtilityId: utility.utilityApiId,
        isTest,
      });
  } else {
    queryFn = () => {};
  }
  const { data, isSuccess }: { data: any; isSuccess: boolean } = useQuery({
    queryFn,
    queryKey: ["do_once"],
  });
  if (isSuccess) {
    if (connType === "bayou") {
      currentAuth.status = "BAYOU_CUSTOMER_CREATED";
      currentAuth.bayouCustomer = data;
      updateLocalStorageAuth(connType, utilityCode, currentAuth);
      if (data?.onboardingLink) {
        window.location.href = data?.onboardingLink;
      }
    } else if (connType === "utilityApi") {
      currentAuth.status = "UTILITYAPI_FORM_CREATED";
      currentAuth.utilityApiForm = data;
      updateLocalStorageAuth(connType, utilityCode, currentAuth);
      if (data?.url) {
        window.location.href = data?.url;
      }
    }
  }

  if (connType === "coned") {
    // Redirect to the ConEd / Orange and Rockland auth page, which will then go to the scope selection page
    const baseUrls = {
      ORU: {
        dev: "https://uat10.oru.com/en/",
        prod: "https://www.oru.com/en/",
      },
      ConEdison: {
        dev: "https://uat10.coned.com/en/",
        prod: "https://www.coned.com/en/",
      },
    };
    const bulkIds = {
      dev: "91",
      prod: "48",
    };
    const currentEnv = utilityEnv();
    const bulkId = bulkIds[currentEnv];
    const baseUrl = baseUrls.ConEdison[currentEnv];
    const redirectUri = UTILITY_REDIRECT_URI[currentEnv];
    const clientIdsByEnv = {
      dev: "0oa21l77rimuWAp9Z0h8",
      prod: "0oa14dlmdcwA0BAju0x8",
    };
    const clientId = clientIdsByEnv[currentEnv];

    const url =
      `${baseUrl}oauth/authorize?scope=FB=1_3_4_5_7_10_15_16_51_53_56_57_58_60;` +
      "IntervalDuration=Monthly_3600_900_300;BlockDuration=Monthly_Daily;HistoryLength=63072000;" +
      `BR=${bulkId}&response_type=code&client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}&state=none`;
    console.log(url);
    if (url) {
      if (isTest) {
        window.location.href = "/external/utility_scope_selection?isTest=1";
      } else {
        window.location.href = url;
      }
    } else {
      console.log("ERROR!");
    }
  }
  return (
    <div className="flex flex-col w-full h-full items-center justify-center">
      <Loader2Icon className="animate-spin" size={80} />
      <div className="text-2xl">
        Creating a connection to {utility?.name}...
      </div>
    </div>
  );
};

function isValidConnType(type: any): type is "bayou" | "utilityApi" | "coned" {
  return ["bayou", "utilityApi", "coned"].includes(type);
}
type NewUtilityConnectionSearch = {
  connType: "bayou" | "utilityApi" | "coned";
  utilityCode: keyof typeof utilities;
  buildingId: string;
  isTest?: boolean;
};
export const Route = createFileRoute("/external/new_utility_connection")({
  component: NewUtilityConnection,
  validateSearch: (
    search: Record<string, string>,
  ): NewUtilityConnectionSearch => {
    if (!isValidConnType(search.conn_type)) {
      throw new Error("Invalid connection type");
    }
    return {
      connType: search.conn_type,
      utilityCode: search.utility_code,
      buildingId: search.buildingId,
      isTest:
        typeof search?.isTest !== "undefined"
          ? Boolean(search?.isTest)
          : undefined,
    };
  },
});
